import { apiUrl, API_BASE } from "../config";

export interface IpDetectionResult {
  backendIp: string | null;
  backendIpVersion: number | null;
  backendGeo: any | null;
  ipifyV4: string | null;
  ipifyV6: string | null;
  webrtcLocalIps: string[];
  fallbackIp: string | null;
}

const BACKEND_SERVERS = [
  "https://nettool-va-ali.rivergame.net",
  "https://nettool-va-gcp.topwargame.com",
  "https://nettool-va-cf.topwarapp.com",
  "https://nettool-va-aws.topwargame.com",
];

async function fetchWithTimeout(url: string, timeoutMs: number = 5000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function getBackendClientInfo(): Promise<{ ip: string; version: number; geo: any } | null> {
  if (API_BASE) {
    try {
      const res = await fetchWithTimeout(apiUrl("/api/client-info"));
      if (res.ok) {
        const data = await res.json();
        return { ip: data.clientIp, version: data.ipVersion, geo: data.geo };
      }
    } catch {}
  }
  for (const base of BACKEND_SERVERS) {
    try {
      const res = await fetchWithTimeout(`${base}/api/client-info`);
      if (res.ok) {
        const data = await res.json();
        return { ip: data.clientIp, version: data.ipVersion, geo: data.geo };
      }
    } catch {}
  }
  return null;
}

async function getIpifyV4(): Promise<string | null> {
  try {
    const res = await fetchWithTimeout("https://api4.ipify.org?format=json", 5000);
    if (!res.ok) return null;
    const data = await res.json();
    return data.ip;
  } catch {
    return null;
  }
}

async function getIpifyV6(): Promise<string | null> {
  try {
    const res = await fetchWithTimeout("https://api6.ipify.org?format=json", 5000);
    if (!res.ok) return null;
    const data = await res.json();
    return data.ip;
  } catch {
    return null;
  }
}

async function getFallbackIp(): Promise<string | null> {
  // Try ip.sb (supports CORS)
  try {
    const res = await fetchWithTimeout("https://api.ip.sb/ip", 5000);
    if (!res.ok) return null;
    const text = await res.text();
    return text.trim() || null;
  } catch {
    return null;
  }
}

export async function getWebRTCLocalIPs(): Promise<string[]> {
  const ips: string[] = [];
  try {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });
    pc.createDataChannel("");
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => resolve(), 3000);
      pc.onicecandidate = (e) => {
        if (!e.candidate) {
          clearTimeout(timeout);
          resolve();
          return;
        }
        const candidate = e.candidate.candidate;
        const ipv4Match = candidate.match(/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/);
        if (ipv4Match && !ips.includes(ipv4Match[1])) {
          ips.push(ipv4Match[1]);
        }
        const ipv6Match = candidate.match(/([0-9a-f]{1,4}(:[0-9a-f]{1,4}){7})/i);
        if (ipv6Match && !ips.includes(ipv6Match[1])) {
          ips.push(ipv6Match[1]);
        }
      };
    });
    pc.close();
  } catch {
    // WebRTC not available
  }
  return ips;
}

export async function detectIp(): Promise<IpDetectionResult> {
  const [backendInfo, ipifyV4, ipifyV6, webrtcIps, fallbackIp] = await Promise.all([
    getBackendClientInfo(),
    getIpifyV4(),
    getIpifyV6(),
    getWebRTCLocalIPs(),
    getFallbackIp(),
  ]);

  return {
    backendIp: backendInfo?.ip ?? null,
    backendIpVersion: backendInfo?.version ?? null,
    backendGeo: backendInfo?.geo ?? null,
    ipifyV4,
    ipifyV6,
    webrtcLocalIps: webrtcIps,
    fallbackIp,
  };
}
